import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import PageContainer from "../components/Common/PageContainer";
import PDFViewer from "../components/Common/PDFViewer";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import InternalError from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import ExtractionDisplay from "../components/Orion/ExtractionDisplay";
import {
  useAsset,
  useDocument,
  useProcessing,
} from "../components/Orion/OrionQueries";
import ProcessingDetails from "../components/Orion/ProcessingDetails";
import ValidationOutput from "../components/Orion/ValidationDisplay";
import AssetPageHeader from "../components/PageHeader/AssetPage";
import ChatPdf from "../components/Sirius/ChatPdf";
import DeleteAssetDocumentDialog from "../components/useDialogs/DeleteAssetDocumentDialog";
import useDialogs from "../components/useDialogs/useDialogs";
import { ObjectId, Status } from "../interfaces/common";
import { DocumentAttrs } from "../interfaces/orion";

const AssetPagev2: React.FC = () => {
  const dialogs = useDialogs();
  // ---Params---
  const { assetId } = useParams();
  // --- State ---
  const {
    data: asset,
    isError: isAssetError,
    isLoading: isAssetLoading,
  } = useAsset(assetId);
  const [processingId, setProcessingId] = useState<ObjectId | undefined>(
    asset?.history[0]?._id
  );
  const {
    data: processing,
    isError: isProcessingError,
    isLoading: isProcessingLoading,
  } = useProcessing(processingId);
  const [document, setDocument] = useState<DocumentAttrs | undefined>(
    processing?.documents[0] ?? asset?.documents[0]
  );
  const { data: documentUrl, isError: isDocumentError } = useDocument(
    document?._id
  );
  const [displayMode, setDisplayMode] = useState<"chat" | "document">(
    "document"
  );

  useEffect(() => {
    if (processing || asset)
      setDocument(processing?.documents[0] ?? asset?.documents[0]);
  }, [asset, processing]);

  useEffect(() => {
    if (asset) setProcessingId(asset.history[0]?._id);
  }, [asset]);

  // --- Rendering ---
  function getContent() {
    if (isAssetLoading || isProcessingLoading) return <Loading />;
    else if (
      !assetId ||
      isAssetError ||
      isProcessingError ||
      isDocumentError ||
      !asset
    )
      return <InternalError />;
    else
      return (
        <Grid2
          container
          spacing={2}
          sx={{ height: "100%", overflow: "hidden" }}
        >
          <Grid2 size={6} sx={{ height: "100%", overflow: "hidden" }}>
            <PDFViewer
              title={document?.name ?? ""}
              fileUrl={documentUrl?.url}
            />
          </Grid2>
          <Grid2 size={6} sx={{ height: "100%", overflow: "hidden" }}>
            {displayMode === "document" ? (
              <Stack className="h-full overflow-hidden px-2">
                <Accordion elevation={3}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="documents-content"
                    id="documents-header"
                    sx={{
                      minHeight: 48,
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                        width: "100%",
                        my: 0,
                      },
                      "&.Mui-expanded": {
                        minHeight: 48,
                      },
                    }}
                  >
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      Documents du dossier
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <CardContent>
                      {asset.documents.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                        >
                          Aucun document dans ce dossier.
                        </Typography>
                      ) : (
                        <List dense>
                          {asset.documents.map((doc) => (
                            <ListItem
                              key={doc._id}
                              divider
                              secondaryAction={
                                <Tooltip title="Supprimer le document">
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={async () =>
                                      await dialogs.open(
                                        DeleteAssetDocumentDialog,
                                        {
                                          id: doc._id,
                                          name: doc.name,
                                          assetId: asset._id,
                                        }
                                      )
                                    }
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              }
                            >
                              <ListItemText
                                primary={doc.name}
                                secondary={`Ajouté le: ${new Date(doc.insertion_date).toLocaleDateString()}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </AccordionDetails>
                </Accordion>
                <Paper elevation={3} className="overflow-auto p-4">
                  {!processing || !processing.review ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      Le dossier n'a pas encore été traité.
                    </Typography>
                  ) : processing.status === Status.PENDING ||
                    processing.status === Status.IN_PROGRESS ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      Traitement en cours...
                    </Typography>
                  ) : (
                    <>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="primary"
                          gutterBottom
                        >
                          Analyse
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {new Date(processing.insertion_date).toLocaleString()}
                        </Typography>
                      </Stack>
                      {processing.review.validation &&
                        _.keys(processing.review.validation.asset_validations)
                          .length > 0 && (
                          <>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="primary"
                              gutterBottom
                              sx={{ mb: 2 }}
                            >
                              Validation
                            </Typography>
                            <ValidationOutput
                              validations={
                                processing.review.validation.asset_validations
                              }
                            />
                            <Divider sx={{ my: 3 }} />
                          </>
                        )}
                      {processing.review.aggregates && (
                        <>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="primary"
                            gutterBottom
                            sx={{ mb: 2 }}
                          >
                            Données aggrégées
                          </Typography>
                          <Stack className="gap-3">
                            {Object.entries(processing.review.aggregates).map(
                              ([ex_id, result]) => (
                                <ExtractionDisplay
                                  key={ex_id}
                                  result={result}
                                />
                              )
                            )}
                          </Stack>
                          <Divider sx={{ my: 3 }} />
                        </>
                      )}

                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        sx={{ mb: 2 }}
                      >
                        Données extraites
                      </Typography>
                      <ProcessingDetails
                        processing={processing}
                        setDocument={setDocument}
                      />
                    </>
                  )}
                </Paper>
                <Typography
                  fontSize={10}
                  fontStyle={"italic"}
                  className="w-full mt-2 text-center"
                >
                  L'analyse peut comporter des erreurs, pensez à vérifier les
                  informations importantes
                </Typography>
              </Stack>
            ) : (
              <ChatPdf
                assetId={assetId}
                processing={processing?.review?.analysis ?? {}}
              />
            )}
          </Grid2>
        </Grid2>
      );
  }

  const HeaderComponent = useCallback(
    () => (
      <AssetPageHeader
        asset={asset}
        processing={processing}
        setProcessingId={setProcessingId}
        displayMode={displayMode}
        setDisplayMode={setDisplayMode}
      />
    ),
    [asset, processing, setProcessingId, displayMode, setDisplayMode]
  );

  return (
    <DashboardLayout>
      <PageContainer
        slots={{ header: HeaderComponent }}
        className="overflow-hidden"
      >
        {getContent()}
      </PageContainer>
    </DashboardLayout>
  );
};

export default AssetPagev2;
